// Live people-management route (PER-007, PER-009; CIR-005, CIR-022; ADM-002..ADM-004;
// bead hf-e3b-circle-people-r3z.1).
//
// Convex subscriptions make capability changes reactive. The view receives the
// server's `canManage` answer; mutations still cross the boundary independently, so
// an already-open browser loses both affordances and write authority on the next
// operation without refreshing a token.

import { Skeleton } from "@herefor/design-system";
import { PEOPLE_MANAGEMENT_MESSAGES as COPY } from "@herefor/product-language";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import {
  peopleMutations,
  peopleQueries,
  personDocumentId,
  type PeopleMutationResult,
} from "../../convex/people";
import { useConnectivity } from "../../offline/offline";
import {
  PeopleManagement,
  type CreatePersonDraft,
  type PeopleOutcome,
  type UpdateMembershipDraft,
  type UpdatePersonDraft,
} from "./PeopleManagement";

export function PeopleRoute({ circleId }: { readonly circleId: string }) {
  const directory = useQuery(peopleQueries.list, { circleId });
  const { actionsPaused } = useConnectivity();
  const createPerson = useMutation(peopleMutations.create);
  const updatePerson = useMutation(peopleMutations.updatePerson);
  const updateMembership = useMutation(peopleMutations.updateMembership);
  const deactivateMembership = useMutation(peopleMutations.deactivate);
  const [outcome, setOutcome] = useState<PeopleOutcome>({ status: "idle" });

  const run = async (
    action: Extract<PeopleOutcome, { status: "saved" }>["action"],
    operation: () => Promise<PeopleMutationResult>,
  ): Promise<boolean> => {
    if (actionsPaused) return false;
    setOutcome({ status: "working" });
    try {
      const result = await operation();
      if (result.status === "replayed" || result.value.ok) {
        setOutcome({ status: "saved", action });
        return true;
      }
      setOutcome({ status: "refused", message: rejectionMessage(result.value.rejection.kind) });
      return false;
    } catch {
      setOutcome({ status: "refused", message: COPY.errors.serviceUnavailable });
      return false;
    }
  };

  if (directory === undefined) {
    return <Skeleton label={COPY.loadingLabel} lines={4} />;
  }

  return (
    <PeopleManagement
      actionsDisabled={actionsPaused}
      directory={directory}
      onCreate={(draft: CreatePersonDraft) =>
        run("created", () =>
          createPerson({
            circleId,
            chosenName: draft.chosenName,
            ...(draft.relationshipLabel === undefined
              ? {}
              : { relationshipLabel: draft.relationshipLabel }),
            preset: draft.preset,
            capabilities: [...draft.capabilities],
            contactMethods: [...draft.contactMethods],
            idempotencyKey: `person-create-${crypto.randomUUID()}`,
          }),
        )
      }
      onDeactivate={(personId: string) =>
        run("deactivated", () =>
          deactivateMembership({ circleId, personId: personDocumentId(personId) }),
        )
      }
      onUpdateMembership={(draft: UpdateMembershipDraft) =>
        run("membership", () =>
          updateMembership({
            circleId,
            personId: personDocumentId(draft.personId),
            preset: draft.preset,
            capabilities: [...draft.capabilities],
          }),
        )
      }
      onUpdatePerson={(draft: UpdatePersonDraft) =>
        run("person", () =>
          updatePerson({
            circleId,
            personId: personDocumentId(draft.personId),
            chosenName: draft.chosenName,
            relationshipLabel: draft.relationshipLabel,
          }),
        )
      }
      outcome={outcome}
    />
  );
}

function rejectionMessage(kind: string): string {
  switch (kind) {
    case "duplicate_contact":
      return COPY.errors.duplicateContact;
    case "last_organiser":
      return COPY.errors.lastOrganiser;
    case "self_grant_forbidden":
    case "capability_grant_exceeds_actor":
      return COPY.errors.validation;
    case "validation_failed":
      return COPY.errors.validation;
    case "not_found":
      return COPY.errors.notFound;
    default:
      return COPY.errors.serviceUnavailable;
  }
}
